import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Voice & Tone Framework Types
export interface VoiceProfile {
  id: string;
  name: string;
  description: string;
  characteristics: string[];
  examples: string[];
  useCase: string;
}

export interface ToneAdaptation {
  audienceType: string;
  toneAdjustments: string[];
  keyConsiderations: string[];
  examples: {
    before: string;
    after: string;
    explanation: string;
  }[];
}

export interface AudienceProfile {
  id: string;
  name: string;
  demographics: string[];
  interests: string[];
  communicationPreferences: string[];
  painPoints: string[];
  motivations: string[];
}

export interface VoiceAndToneState {
  // User's personal voice profile
  personalVoice: VoiceProfile | null;
  
  // Available tone adaptations
  toneAdaptations: ToneAdaptation[];
  
  // Audience profiles for targeting
  audienceProfiles: AudienceProfile[];
  
  // Current lesson progress
  currentLesson: string | null;
  lessonProgress: Record<string, number>;
  
  // Workshop outputs
  voiceExercises: Record<string, any>;
  completedAnalyses: string[];
}

interface VoiceAndToneContextType extends VoiceAndToneState {
  // Voice profile actions
  setPersonalVoice: (voice: VoiceProfile) => void;
  updatePersonalVoice: (updates: Partial<VoiceProfile>) => void;
  
  // Tone adaptation actions
  addToneAdaptation: (adaptation: ToneAdaptation) => void;
  updateToneAdaptation: (audienceType: string, updates: Partial<ToneAdaptation>) => void;
  
  // Audience profile actions
  addAudienceProfile: (profile: AudienceProfile) => void;
  updateAudienceProfile: (id: string, updates: Partial<AudienceProfile>) => void;
  
  // Progress tracking
  setCurrentLesson: (lessonId: string | null) => void;
  updateLessonProgress: (lessonId: string, progress: number) => void;
  markLessonComplete: (lessonId: string) => void;
  
  // Exercise and analysis tracking
  saveVoiceExercise: (exerciseId: string, data: any) => void;
  markAnalysisComplete: (analysisId: string) => void;
  
  // Utility functions
  resetProgress: () => void;
  exportVoiceProfile: () => string;
}

const VoiceAndToneContext = createContext<VoiceAndToneContextType | undefined>(undefined);

// Default voice profiles for Maya's framework
const defaultVoiceProfiles: VoiceProfile[] = [
  {
    id: 'authentic-advocate',
    name: 'Authentic Advocate',
    description: 'Passionate, genuine, and mission-driven communication',
    characteristics: ['Passionate', 'Genuine', 'Mission-focused', 'Empathetic'],
    examples: [
      'We believe every child deserves a safe place to learn and grow',
      'Together, we can transform our community one family at a time'
    ],
    useCase: 'Donor communications, mission statements, community outreach'
  },
  {
    id: 'collaborative-connector',
    name: 'Collaborative Connector',
    description: 'Partnership-focused, inclusive, and solution-oriented',
    characteristics: ['Inclusive', 'Partnership-minded', 'Solution-focused', 'Respectful'],
    examples: [
      'Let\'s explore how we can work together to address this challenge',
      'Your expertise combined with our community presence creates powerful impact'
    ],
    useCase: 'Partner communications, stakeholder meetings, collaborative projects'
  }
];

const initialState: VoiceAndToneState = {
  personalVoice: null,
  toneAdaptations: [],
  audienceProfiles: [],
  currentLesson: null,
  lessonProgress: {},
  voiceExercises: {},
  completedAnalyses: []
};

export const VoiceAndToneProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<VoiceAndToneState>(initialState);

  // Voice profile actions
  const setPersonalVoice = useCallback((voice: VoiceProfile) => {
    setState(prev => ({ ...prev, personalVoice: voice }));
  }, []);

  const updatePersonalVoice = useCallback((updates: Partial<VoiceProfile>) => {
    setState(prev => ({
      ...prev,
      personalVoice: prev.personalVoice ? { ...prev.personalVoice, ...updates } : null
    }));
  }, []);

  // Tone adaptation actions
  const addToneAdaptation = useCallback((adaptation: ToneAdaptation) => {
    setState(prev => ({
      ...prev,
      toneAdaptations: [...prev.toneAdaptations, adaptation]
    }));
  }, []);

  const updateToneAdaptation = useCallback((audienceType: string, updates: Partial<ToneAdaptation>) => {
    setState(prev => ({
      ...prev,
      toneAdaptations: prev.toneAdaptations.map(adaptation =>
        adaptation.audienceType === audienceType
          ? { ...adaptation, ...updates }
          : adaptation
      )
    }));
  }, []);

  // Audience profile actions
  const addAudienceProfile = useCallback((profile: AudienceProfile) => {
    setState(prev => ({
      ...prev,
      audienceProfiles: [...prev.audienceProfiles, profile]
    }));
  }, []);

  const updateAudienceProfile = useCallback((id: string, updates: Partial<AudienceProfile>) => {
    setState(prev => ({
      ...prev,
      audienceProfiles: prev.audienceProfiles.map(profile =>
        profile.id === id ? { ...profile, ...updates } : profile
      )
    }));
  }, []);

  // Progress tracking
  const setCurrentLesson = useCallback((lessonId: string | null) => {
    setState(prev => ({ ...prev, currentLesson: lessonId }));
  }, []);

  const updateLessonProgress = useCallback((lessonId: string, progress: number) => {
    setState(prev => ({
      ...prev,
      lessonProgress: { ...prev.lessonProgress, [lessonId]: progress }
    }));
  }, []);

  const markLessonComplete = useCallback((lessonId: string) => {
    setState(prev => ({
      ...prev,
      lessonProgress: { ...prev.lessonProgress, [lessonId]: 100 }
    }));
  }, []);

  // Exercise and analysis tracking
  const saveVoiceExercise = useCallback((exerciseId: string, data: any) => {
    setState(prev => ({
      ...prev,
      voiceExercises: { ...prev.voiceExercises, [exerciseId]: data }
    }));
  }, []);

  const markAnalysisComplete = useCallback((analysisId: string) => {
    setState(prev => ({
      ...prev,
      completedAnalyses: [...prev.completedAnalyses, analysisId]
    }));
  }, []);

  // Utility functions
  const resetProgress = useCallback(() => {
    setState(initialState);
  }, []);

  const exportVoiceProfile = useCallback(() => {
    return JSON.stringify(state, null, 2);
  }, [state]);

  const contextValue: VoiceAndToneContextType = {
    ...state,
    setPersonalVoice,
    updatePersonalVoice,
    addToneAdaptation,
    updateToneAdaptation,
    addAudienceProfile,
    updateAudienceProfile,
    setCurrentLesson,
    updateLessonProgress,
    markLessonComplete,
    saveVoiceExercise,
    markAnalysisComplete,
    resetProgress,
    exportVoiceProfile
  };

  return (
    <VoiceAndToneContext.Provider value={contextValue}>
      {children}
    </VoiceAndToneContext.Provider>
  );
};

export const useVoiceAndTone = () => {
  const context = useContext(VoiceAndToneContext);
  if (context === undefined) {
    throw new Error('useVoiceAndTone must be used within a VoiceAndToneProvider');
  }
  return context;
};